import { AbilityBuilder, createMongoAbility, PureAbility } from '@casl/ability'

import { CaseWorker, User } from '@/store/user'

export function defineAbilitiesFor(entities: { user?: User; caseWorker?: CaseWorker }): AppAbility {
  const { build, ...helpers } = createTypedAbilityBuilder()

  const user = 'user' in entities ? entities.user : undefined
  const caseWorker = 'caseWorker' in entities ? entities.caseWorker : undefined

  if (user) {
    defineUserAbilities(helpers, user)
  }

  if (caseWorker) {
    defineCaseWorkerAbilities(helpers, caseWorker)
  }

  return build()
}

function defineUserAbilities(helpers: Helpers, user: User) {
  const { can, cannot } = helpers

  can('accessRoute', 'all')
  cannot('accessRoute', ['admin', 'demo', 'super-admin']) // Restrict access to special routes

  if (user.superAdmin) {
    can('accessRoute', 'all')
    can('manage', 'all') // 'manage' represents any action, 'all' represents any subject
    cannot('delete', 'User', { id: user.id }) // Admin cannot delete themselves
    cannot('disable', 'User', { id: user.id }) // Admin cannot disable themselves
  }

  if (user.email.endsWith('@bayesimpact.org')) {
    can('create', 'Organization')
  }
}

function defineCaseWorkerAbilities(helpers: Helpers, caseWorker: CaseWorker) {
  const { can, cannot } = helpers
  // Can't import shared enum in this file
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  if (caseWorker.role === 'admin') {
    can('read', 'all')
    can('accessRoute', 'admin')
    can('delete', 'Beneficiary')
    cannot('disable', 'CaseWorker', { id: caseWorker.id }) // Admin cannot disable themselves
    cannot('delete', 'CaseWorker', { id: caseWorker.id }) // Admin cannot delete themselves
    can('manage', 'RefreshAllStructuredData')
    can('manage', 'Insights')
  }
}

type Helpers = Omit<ReturnType<typeof createTypedAbilityBuilder>, 'build'>

export type Actions =
  | 'accessRoute'
  | 'create'
  | 'delete'
  | 'disable'
  | 'enable'
  | 'manage'
  | 'read'
  | 'update'
  | 'execute' // execute store actions

// Split subjects into categories

type RouteSubjects = 'admin' | 'demo' | 'super-admin' | 'all'

type EntitySubjects = 'User' | 'CaseWorker' | 'Beneficiary' | 'Organization' | 'all'

type FeatureSubjects = 'Insights' | 'RefreshAllStructuredData'

// Combined type for internal use

type Subjects = RouteSubjects | EntitySubjects | FeatureSubjects

// Create a mapping between actions and their allowed subject types
interface ActionSubjectMap {
  accessRoute: RouteSubjects
  create: EntitySubjects
  delete: EntitySubjects
  disable: EntitySubjects
  enable: EntitySubjects
  manage: Subjects
  read: EntitySubjects
  update: EntitySubjects
}

export type AppAbility = PureAbility<[Actions, Subjects]>

function createTypedAbilityBuilder() {
  const baseBuilder = new AbilityBuilder<AppAbility>(createMongoAbility)

  return {
    ...baseBuilder,
    can<A extends Actions>(
      action: A,
      subject: A extends keyof ActionSubjectMap
        ? ActionSubjectMap[A] | ActionSubjectMap[A][]
        : Subjects | Subjects[],
      conditions?: unknown,
    ) {
      return baseBuilder.can(action, subject, conditions)
    },
    cannot<A extends Actions>(
      action: A,
      subject: A extends keyof ActionSubjectMap
        ? ActionSubjectMap[A] | ActionSubjectMap[A][]
        : Subjects | Subjects[],
      conditions?: unknown,
    ) {
      return baseBuilder.cannot(action, subject, conditions)
    },
    build: baseBuilder.build,
  }
}
